import { format } from "@formkit/tempo"

export function paginateArray<T>(array: T[], itemsPerPage: number): T[][] {
  return array.reduce((result: T[][], item: T, index: number) => {
    const pageIndex: number = Math.floor(index / itemsPerPage)

    if (!result[pageIndex]) {
      result[pageIndex] = [] // create a new page if it doesn't exist
    }

    result[pageIndex].push(item)

    return result
  }, [])
}

export function modifyEmbedString(string: string) {
  // Replace height and width attributes and add the new class
  const modifiedJsxString = string
    .replace(/height="\d+"/g, "") // Remove height attributes
    .replace(/width="\d+"/g, "") // Remove width attributes
    .replace(
      /(<div[^>]+)(class="[^"]*")?([^>]*>)/g,
      '$1class="w-full h-[100vh]"$3',
    ) // Add or replace class attribute

  return modifiedJsxString
}

export function videoEmbedString(string: string) {
  // Replace height and width attributes and add the new class
  const videfiedJsxString = string
    .replace(/height="\d+"/g, "") // Remove height attributes
    .replace(/width="\d+"/g, "") // Remove width attributes
    .replace(
      /(<div[^>]+)(class="[^"]*")?([^>]*>)/g,
      '$1class="w-full h-[100vh]"$3',
    ) // Add or replace class attribute

  return videfiedJsxString
}

export function calculateReadingTime(text: string) {
  // Assuming an average reading speed of 200 words per minute
  const wordsPerMinute = 100

  // Counting the number of words in the text
  const wordCount = text.split(/\s+/).length

  // Calculating the reading time in minutes
  const readingTimeMinutes = Math.ceil(wordCount / wordsPerMinute)

  // Converting the reading time to seconds
  const readingTimeSeconds = readingTimeMinutes * 60

  // Formatting the result in "15s" format
  const formattedReadingTime = `${readingTimeSeconds}s`

  return formattedReadingTime
}

export function formatDate(date: string) {
  if (!date) return ""
  const dateObj = new Date(date)
  //transform date from yyyy-mm-dd to dd/mm/yyyy
  const formattedDate = dateObj.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })

  return formattedDate
}

export function formatDateUTC(date: string) {
  if (!date) return ""
  const dateObj = new Date(date)
  const formattedDate = dateObj.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  })

  return formattedDate
}

export function formatBirthday(date: string) {
  if (!date) return ""
  const dateObj = new Date(date)
  const formattedDate = dateObj.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
  })

  return formattedDate
}

export function formatBirthdayMonth(date: string) {
  if (!date) return ""
  const dateObj = new Date(date)
  const formattedDate = dateObj.toLocaleDateString("es-CO", {
    month: "numeric",
    timeZone: "UTC",
  })

  return formattedDate
}

export function formatBirthdayUTC(date: string) {
  if (!date) return ""
  const dateObj = new Date(date)
  const formattedDate = dateObj.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  })

  return formattedDate
}

export function formatDateWithTime(date: string) {
  if (!date) return ""
  const dateObj = new Date(date)
  //transform date from yyyy-mm-dd to dd/mm/yyyy
  const formattedDate = dateObj.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  })

  return formattedDate
}

export function getUniqueValues(data) {
  const uniqueValues = {
    year: [],
    retentionType: [],
  }

  if (!data) {
    return uniqueValues
  }

  data.forEach((item) => {
    if (!uniqueValues.year.includes(item.year)) {
      uniqueValues.year.push(item.year)
    }
    if (!uniqueValues.retentionType.includes(item.retentionType)) {
      uniqueValues.retentionType.push(item.retentionType)
    }
  })

  return uniqueValues
}

export function createContainRegexFromString(str: string) {
  const vowels: Record<string, string> = {
    a: "[aáàäâãå]",
    á: "[aáàäâãå]",
    à: "[aáàäâãå]",
    ä: "[aáàäâãå]",
    â: "[aáàäâãå]",
    ã: "[aáàäâãå]",
    å: "[aáàäâãå]",

    e: "[eéèêë]",
    é: "[eéèêë]",
    è: "[eéèêë]",
    ê: "[eéèêë]",
    ë: "[eéèêë]",

    i: "[iíìîï]",
    í: "[iíìîï]",
    ì: "[iíìîï]",
    î: "[iíìîï]",
    ï: "[iíìîï]",

    o: "[oóòôõö]",
    ó: "[oóòôõö]",
    ò: "[oóòôõö]",
    ô: "[oóòôõö]",
    õ: "[oóòôõö]",
    ö: "[oóòôõö]",

    u: "[uúùûü]",
    ú: "[uúùûü]",
    ù: "[uúùûü]",
    û: "[uúùûü]",
    ü: "[uúùûü]",
  }

  const regex = /[aáàäâãåeéèêëiíìîïoóòôõöuúùûü]/gi

  const escapedStr = str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // Escapa caracteres especiales de regex

  return new RegExp(
    escapedStr.replace(regex, (match) => vowels[match.toLowerCase()] || match),
    "i", // Hace que la búsqueda no sea sensible a mayúsculas/minúsculas
  )
}

export function capitalizeFirstLetterRegex(
  str: string,
  exceptionWords: string[] = ["los", "de", "del"],
) {

  if (!str) return ""
  str = str.toString()
  
  const regex = new RegExp(`(?:^|\\s)(?!${exceptionWords.join("|")})(\\S)`, "g")
  return str.toLocaleLowerCase().replace(regex, function (match) {
    return match.toUpperCase()
  })
}

export const isValidUrl = (url: string) => {
  try {
    new URL(url)
    return true
  } catch (e) {
    return false
  }
}

export const normalizeUrl = (url: string) => {
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return `https://${url}`
  }
  return url
}

export const getDisplayUrl = (url: string) => {
  return url.replace(/^https?:\/\//, "")
}

export function objectToQueryString(
  obj: Record<string, string | number | boolean | undefined | null>,
) {
  const queryParams = []

  for (let key in obj) {
    if (
      obj.hasOwnProperty(key) &&
      obj[key] !== "" &&
      obj[key] !== undefined &&
      obj[key] !== null
    ) {
      const encodedKey = encodeURIComponent(key)
      const encodedValue = encodeURIComponent(
        obj[key] as string | number | boolean,
      )
      queryParams.push(encodedKey + "=" + encodedValue)
    }
  }

  if (queryParams.length === 0) return ""

  return "?" + queryParams.join("&")
}

export const reportFormatDate = (dateString) => {
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export const getBuText = (buCode?: string, buCodes) => {
  if (!buCode) return ""
  return buCodes?.find((buData: any) => buData.code == buCode)?.text || ""
}

export const flattenObject = (obj) => {
  const flattened = {}

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      const flatObject = flattenObject(obj[key])
      Object.keys(flatObject).forEach((childKey) => {
        flattened[`${key}.${childKey}`] = flatObject[childKey]
      })
    } else {
      flattened[key] = obj[key]
    }
  })

  return flattened
}

export const getNumberOfWeeksInMonth = (year: number, month: number) => {
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  const firstDayWeek = firstDay.getDay();
  const lastDayWeek = lastDay.getDay();
  const weeks = Math.ceil((daysInMonth + firstDayWeek - 1) / 7);
  return weeks;
};

export const getStartOfWeek = (year: number, weekNumber: number) => {
  const oneJan = new Date(year, 0, 1);
  const dayOfYear = (weekNumber - 1) * 7 + 1 - (oneJan.getDay() === 0 ? 6 : oneJan.getDay() - 1);
  const date = new Date(year, 0, dayOfYear);
  return format(date, "long", "es")
}

export const getEndOfWeek = (year: number, weekNumber: number) => {
  const oneJan = new Date(year, 0, 1);
  const dayOfYear = (weekNumber - 1) * 7 + 7 - (oneJan.getDay() === 0 ? 6 : oneJan.getDay() - 1);
  const date = new Date(year, 0, dayOfYear);
  return format(date, "long", "es")
}

export const getCurrentYear = () => new Date().getFullYear();
export const getCurrentMonth = () => new Date().getMonth() + 1;
export const getCurrentWeek = (date: Date) => {
  const oneJan = new Date(date.getFullYear(), 0, 1);
  const dayOfYear = Math.floor(
    (date.getTime() - oneJan.getTime()) / (1000 * 60 * 60 * 24)
  );
  return Math.ceil((dayOfYear + (oneJan.getDay() === 0 ? 6 : oneJan.getDay() - 1) + 1) / 7);
};